import {
    UniversalDataTable,
    UniversalModal,
    UniversalToolBar,
    UniversalCookieCrumb,
    UniversalLoadingCircle,
    DataGridLayout,
    Grid,
} from '../../../UniversalComponents'
import { useReducer, useEffect, useContext, useCallback } from 'react'
import { AddBusiness as AddBusinessIcon } from '@mui/icons-material'
import DealersForm from './DealersForm'
import {
    Archive as ArchiveIcon,
    Restore,
    DeleteForever,
} from '@mui/icons-material'
import {
    getDealers,
    activateDealers,
    deleteDealers,
    getDealersWithLimiter,
} from '../../../../apiRoutes'

import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../../context/MinimizeContext'
import {
    standardColumnMapper,
    columnMapper,
    permissionsCheck,
    handleActivation,
    handleDeletion,
    useDebounce,
    universalDateConverter,
} from '../../../../utilities/functionTools'
import {
    usePermissions,
    useAuth,
} from '../../../../context/AuthenticationContext'
import { useGlobalState } from '../../../../context/GlobalStateContext'

const today = new Date().toISOString().substring(0, 10)
const Dealers = () => {
    const currentUser = useAuth()
    const { setUser } = useAuth()
    const { activeManufacturer, username, userPreferences, _id } =
        currentUser.auth
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { userProfile, userEmail } = oupUser
    const { superAdmin } = userProfile

    const defaultState = {
        open: false,
        formCache: {},
        row: {},
        data: [],
        activateData: [],
        viewActive: true,
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        dealers: {
            columns: [
                {
                    field: 'dealerName',
                    headerName: 'NAME',
                    width: 150,
                },
                {
                    field: 'dealerAddress',
                    headerName: 'ADDRESS',
                    width: 150,
                },
                {
                    field: 'dealerEmail',
                    headerName: 'EMAIL',
                    width: 150,
                },
                {
                    field: 'dealerPhoneNumber',
                    headerName: 'PHONE NUMBER',
                    width: 150,
                },
            ],
            density: 'standard',
        },
        density:
            userPreferences?.dataTableColumns?.dealers?.density || 'standard',
    }
    const setToastMessage = useContext(ToastMessage)
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const { setMinimized } = useMinimizer()

    const {
        open,
        row,
        add,
        data,
        activateData,
        viewActive,
        formCache,
        dealers,
        density,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
    } = state

    const userPermissions = usePermissions()

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.dealerName) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({ row: defaultState.row, open: !open })
        }
        getData(viewActive)
    }

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache.dealerName
                ? `Edit: ${formCache.dealerName}`
                : 'Add New Dealer',
            link: '/admin/Dealers',
            data: formCache.id
                ? { ...formCache, tab: 'dealer' }
                : { ...formCache, id: 'dealer', tab: 'dealer' },
            id: formCache.id ? formCache.id : 'dealer',
        })
        setState({ open: false })
    }

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let dealerData: any
        try {
            const dealers = await getDealersWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })

            dealerData = Array.isArray(dealers?.dealers) ? dealers.dealers : []
            const total = Number(dealers?.total) || 0

            if (
                dealerData?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No Dealers found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    loading: false,
                    noResults: true,
                })
                return
            }
            dealerData = standardColumnMapper(
                defaultColumns,
                dealers?.dealers
            ).map((item: any) => ({
                ...item,
                id: item?._id,
            }))

            setState({
                data: dealerData,
                rowCount: total,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, users: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const dealers = userPreferences?.dataTableColumns?.dealers?.columns

        if (Array.isArray(dealers)) {
            const mapColumns = dealers.map((dealer) => ({
                field: dealer?.field,
                headerName: dealer?.headerName,
                width: dealer?.width,
                isVisible: dealer.isVisible,
            }))

            const format = {
                columns: mapColumns,
            }

            setState({ ...state, dealers: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'dealers',
                value: dealers,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleArchiveData = async () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'dealerName',
            setState,
            getData,
            route: activateDealers,
            viewDeactivated: !viewActive,
            setToastMessage,
            page: 'dealers',
        })
    }
    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'dealerName',
            setState,
            getData,
            route: deleteDealers,
            viewDeactivated: !viewActive,
            setToastMessage,
            page: 'dealers',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these Dealers?`,
            'warning',
            handleDeletionData
        )
    }
    const setView = () => setState({ viewActive: !viewActive })

    useEffect(() => {
        collectColumnData()
        getData(true)
    }, [])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    useEffect(() => {
        setState({ paginationModel: { page: 0, pageSize: 50 } })
        getData(viewActive)
    }, [viewActive, activeManufacturer])

    const defaultColumns = [
        'dealerName',
        'dealerAddress',
        'dealerEmail',
        'dealerPhoneNumber',
        'dealerSalesContactName',
        'dealerSalesContactEmail',
        'dealerSalesContactPhone',
        'dealerServiceContactName',
        'dealerServiceContactEmail',
        'dealerServiceContactPhone',
        'dealerLatitude',
        'dealerLongitude',
        'archived',
        'updatedBy',
        'updatedAt',
    ]

    const buttons = [
        {
            buttonName: 'Add New Dealer',
            buttonIcon: <AddBusinessIcon />,
            buttonFunction: () => setState({ open: !open }),
            disabled: permissionsCheck(userPermissions, 'Dealers'),
        },

        {
            buttonName: viewActive ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleArchiveData,
            disabled: permissionsCheck(userPermissions, 'Dealers'),
        },
        {
            buttonName: viewActive
                ? 'View Deactivated Dealers'
                : 'View Active Dealers',
            buttonIcon: <Restore />,
            buttonFunction: setView,
        },

        {
            buttonName: 'Delete Dealers',
            buttonIcon: <DeleteForever />,
            buttonFunction: handleDeletionCheck,

            disabled: superAdmin && superAdmin === true ? false : true,
            display: viewActive ? 'none' : null,
        },

        // {
        //     buttonName: 'Export',
        //     buttonIcon: <ArrowOutwardIcon />,
        //     buttonFunction: () => {},
        // },
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
        }))

        const setDensity = state.density

        const setData = { columns: format, density: setDensity }

        setState({
            dealers: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            dealers: {
                ...dealers,
                density: newDensity,
            },
        })
    }

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>
            <Grid item xs={12}>
                <UniversalLoadingCircle
                    data={data}
                    reloadFunction={
                        viewActive ? () => getData(true) : () => getData(false)
                    }
                    customTimeoutMessage={
                        !viewActive
                            ? 'No Active Dealers Available'
                            : 'No Deactivated Dealers Available'
                    }
                >
                    <UniversalDataTable
                        data={data}
                        apiDensity={density}
                        apiColumns={dealers?.columns}
                        savePreferences={() => submitColumnData()}
                        visibleColumns={[]}
                        getRowData={(tableRow) =>
                            setModalVisbility(tableRow, undefined)
                        }
                        onRowSelectionModelChange={(newSelection) => {
                            setState({ activateData: newSelection })
                        }}
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        loading={loading}
                        paginationMode={'server'}
                        pageSizeOptions={[50]}
                        setPaginationModel={(data) => {
                            setState({ paginationModel: data })
                        }}
                        onApiSearch={(text) => {
                            setState({ noResults: false })
                            onApiSearch(text)
                        }}
                    />
                </UniversalLoadingCircle>
            </Grid>

            <UniversalModal
                title={row.dealerName ? 'Edit New Dealer' : 'Add New Dealer '}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '95%',
                    marginTop: 5,
                }}
                maxWidth="lg"
                timeStamp={`${
                    row?.id
                        ? ` Modified By: ${row?.updatedBy} ${
                              row?.updatedAt
                                  ? `| Modified Date ${universalDateConverter(
                                        row?.updatedAt
                                    )}`
                                  : ''
                          }  `
                        : ` Created by: ${userEmail} | Modified Date: ${today} `
                }`}
                onMinimize={minimizeModal}
                draggable={true}
                minimizable={true}
            >
                <DealersForm
                    formTitle="Edit Dealer Details"
                    row={row}
                    onClose={(tableRow: any, newData: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    permissionCheck={permissionsCheck(
                        userPermissions,
                        'Dealers'
                    )}
                />
            </UniversalModal>
        </Grid>
    )
}

export default Dealers
